import type { Permission } from '@kortex/shared';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { initials, titleCase } from '../lib/format';
import { useToast } from './ui';

interface NavEntry {
  to: string;
  label: string;
  icon: string;
  permission: Permission;
  group: string;
}

const NAV: NavEntry[] = [
  { group: 'Operations', to: '/', label: 'Dashboard', icon: '◈', permission: 'dashboard:view' },
  { group: 'Operations', to: '/pos', label: 'POS Billing', icon: '▤', permission: 'pos:operate' },
  { group: 'Operations', to: '/kitchen', label: 'Kitchen Display', icon: '♨', permission: 'kds:view' },
  { group: 'Operations', to: '/front-desk', label: 'Front Desk', icon: '⌂', permission: 'rooms:view' },
  { group: 'Operations', to: '/reservations', label: 'Reservations', icon: '◷', permission: 'reservation:view' },
  { group: 'Operations', to: '/housekeeping', label: 'Housekeeping', icon: '✦', permission: 'housekeeping:view' },
  { group: 'Operations', to: '/invoices', label: 'Invoices', icon: '❑', permission: 'invoice:view' },
  { group: 'Kitchen & Stock', to: '/menu', label: 'Menu & Pricing', icon: '☰', permission: 'menu:view' },
  { group: 'Kitchen & Stock', to: '/inventory', label: 'Inventory', icon: '⛁', permission: 'inventory:view' },
  { group: 'Kitchen & Stock', to: '/procurement', label: 'Procurement', icon: '⇄', permission: 'procurement:view' },
  { group: 'People', to: '/people', label: 'Staff & Payroll', icon: '☺', permission: 'hr:view' },
  { group: 'Insight', to: '/reports', label: 'Reports', icon: '◱', permission: 'reports:view' },
  { group: 'Insight', to: '/gst', label: 'GST Filing', icon: '₹', permission: 'gst:export' },
  { group: 'Administration', to: '/users', label: 'Users & Roles', icon: '⚿', permission: 'users:view' },
  { group: 'Administration', to: '/settings', label: 'Settings', icon: '⚙', permission: 'settings:view' },
  { group: 'Administration', to: '/audit', label: 'Audit Log', icon: '≡', permission: 'audit:view' },
];

export function Layout() {
  const { session, signOut, switchProperty, can } = useAuth();
  const { fail } = useToast();
  const location = useLocation();
  const [propertyMenu, setPropertyMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('kortex-theme') ?? 'light');
  const propertyRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('kortex-theme', theme);
  }, [theme]);

  useEffect(() => {
    setNavOpen(false);
    setPropertyMenu(false);
    setUserMenu(false);
  }, [location.pathname]);

  // Close whichever popover is open when the click lands outside it.
  useEffect(() => {
    if (!propertyMenu && !userMenu) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (propertyMenu && !propertyRef.current?.contains(target)) setPropertyMenu(false);
      if (userMenu && !userRef.current?.contains(target)) setUserMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [propertyMenu, userMenu]);

  if (!session) return null;

  const visible = NAV.filter((entry) => can(entry.permission));
  const groups = [...new Set(visible.map((entry) => entry.group))];
  const current = visible.find((entry) => entry.to === location.pathname);

  const onSwitch = async (propertyId: string) => {
    try {
      await switchProperty(propertyId);
    } catch (error) {
      fail(error);
    }
  };

  return (
    <div className="app">
      <aside className={`sidebar no-print ${navOpen ? 'open' : ''}`}>
        <div className="brand">
          <div className="brand-mark">K</div>
          <div>
            <div className="brand-name">Kortex HMS</div>
            <div className="brand-sub">{session.property.code ?? 'Property'}</div>
          </div>
        </div>

        {session.properties.length > 1 ? (
          <div style={{ position: 'relative' }} ref={propertyRef}>
            <button className="switcher" onClick={() => setPropertyMenu((open) => !open)}>
              <div>
                <div className="sw-name">{session.property.name}</div>
                <div className="sw-role">{titleCase(session.role)}</div>
              </div>
              <span className="sw-caret">⌄</span>
            </button>
            {propertyMenu ? (
              <div className="menu-pop" style={{ left: 10, right: 10, top: 'calc(100% + 2px)' }}>
                <div className="head">Switch property</div>
                {session.properties.map((property) => (
                  <button key={property.id} onClick={() => void onSwitch(property.id)}>
                    <span>{property.name}</span>
                    <span className="xs muted" style={{ marginLeft: 'auto' }}>
                      {titleCase(property.role)}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="switcher" style={{ cursor: 'default' }}>
            <div>
              <div className="sw-name">{session.property.name}</div>
              <div className="sw-role">{titleCase(session.role)}</div>
            </div>
          </div>
        )}

        <nav className="nav">
          {groups.map((group) => (
            <div key={group}>
              <div className="nav-group">{group}</div>
              {visible
                .filter((entry) => entry.group === group)
                .map((entry) => (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    end={entry.to === '/'}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  >
                    <span className="ico">{entry.icon}</span>
                    <span>{entry.label}</span>
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="row between">
            <span>{session.property.gstin}</span>
            <button
              className="btn ghost sm"
              style={{ color: '#9fc4c0' }}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title="Toggle theme"
            >
              ◐
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar no-print">
          <button
            className="btn ghost icon sm"
            onClick={() => setNavOpen((open) => !open)}
            style={{ display: 'none' }}
            aria-label="Menu"
          >
            ☰
          </button>
          <div>
            <div className="title">{current?.label ?? 'Kortex HMS'}</div>
            <div className="crumb">{session.property.name}</div>
          </div>
          <div className="grow" />
          <div style={{ position: 'relative' }} ref={userRef}>
            <button className="user-chip" onClick={() => setUserMenu((open) => !open)}>
              <span className="avatar">{initials(session.user.name)}</span>
              <span className="small b">{session.user.name}</span>
              <span className="muted">⌄</span>
            </button>
            {userMenu ? (
              <div className="menu-pop">
                <div className="head">{session.user.email}</div>
                <div className="sep" />
                <button onClick={() => void signOut()}>Sign out</button>
              </div>
            ) : null}
          </div>
        </header>

        <main className="view">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="view-head">
      <div>
        <h1>{title}</h1>
        {subtitle ? <div className="sub">{subtitle}</div> : null}
      </div>
      {actions ? <div className="row gap-6">{actions}</div> : null}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="page-tabs">
      {tabs.map((tab) => (
        <button key={tab.id} className={tab.id === active ? 'active' : ''} onClick={() => onChange(tab.id)}>
          {tab.label}
        </button>
      ))}
    </div>
  );
}
